import { useCallback, MutableRefObject } from 'react';
import { ExtendedReturnItem, ReturnItemProperty } from 'store/reducers/shared/sliceTypes';
import { ReturnOrderLine, ReturnSlip, ReturnItem, ReturnOrderLineType } from 'returnCenter-api';
import { ThemeColors } from 'styled-components';
import { TableRow } from 'components/ui/Table';
import { alignFlexStart, canSplitItem, getCellPadding } from 'utils/helpers';

import ProductInfoCell from 'components/TableCells/ProductInfoCell';
import SkuLocationCell from 'components/TableCells/SkuLocationCell';
import OrderLineNumberCell from 'components/TableCells/OrderLineNumberCell';
import ShippedQtyCell from 'components/TableCells/ShippedQtyCell';
import ReturnQtyCell from 'components/TableCells/ReturnQtyCell';
import QuantityRequestedCell from 'components/TableCells/QuantityRequestedCell';
import ApprovedStatusCell from 'components/TableCells/ApprovedStatusCell';
import ReasonCell from 'components/TableCells/ReasonCell';
import RefundCell from 'components/TableCells/RefundCell';
import DecisionCodeCell from 'components/TableCells/DecisionCodeCell';
import ReturnToStockCell from 'components/TableCells/ReturnToStockCell';
import ReturnsOnThisLineCell from 'components/TableCells/ReturnsOnThisLineCell';
import CommentIndicatorCell from 'components/TableCells/CommentIndicatorCell';
import BatchReturnIndicatorCell from 'components/TableCells/BatchReturnIndicatorCell';
import RowActionsMenu from 'components/ui/RowActionsMenu';
import { useTranslation } from 'react-i18next';
import { faMessageDots } from '@fortawesome/pro-light-svg-icons';
import { CellInputRefs } from './useKeyboardNavigation';
import LpnCell from 'components/TableCells/LpnCell';

interface RowRefs {
    [key: string]: HTMLDivElement | null;
}

interface RowInputRefs {
    [key: string]: HTMLInputElement | null;
}

export type HandleReturnItemChange = (
    orderNumber: string,
    sku: string,
    orderLineNumber: string,
    itemId: number | string,
    property: ReturnItemProperty,
    value: number | null | boolean | string,
    sourceReturnId?: number | null,
) => void;

export type HandleAddSplitItem = (
    orderNumber: string,
    item: ReturnItem,
    itemId: number | string,
    orderline: ReturnOrderLine,
    sourceReturnId?: number | null,
) => void;

export type HandleRemoveSplit = (
    sku: string,
    orderNumber: string,
    orderLineNumber: string,
    id?: string | number,
    rowIndex?: number,
    colIndex?: number,
    cellRefPrefix?: string,
    sourceReturnId?: number | null,
) => void;

export type HandleArrowKeyDown = (
    e: React.KeyboardEvent,
    itemId: string | number,
    rowIndex: number,
    colIndex: number,
    orderId?: string,
) => void;

export type OnOpenComments = (orderNumber: string, orderline: ReturnOrderLine, returnItem: ExtendedReturnItem) => void;

export type OnOpenBatchDrawer = (
    orderNumber: string,
    orderline: ReturnOrderLine,
    returnItem: ExtendedReturnItem,
) => void;

interface UseOrderTableRowsOptions {
    returnSlip: ReturnSlip | null | undefined;
    searchQuery: string;
    isCompletedOrHasError: boolean;
    isCreditedBatchPending: boolean;
    hasDecisionCodes: boolean;
    isPublicReturn: boolean;
    hasBatchReturnItems: boolean;
    showLpn: boolean;
    origin: string | null;
    handleReturnItemChange: HandleReturnItemChange;
    handleAddSplitItem: HandleAddSplitItem;
    handleRemoveSplit: HandleRemoveSplit;
    handleArrowKeyDown: HandleArrowKeyDown;
    onOpenComments: OnOpenComments;
    onOpenBatchDrawer: OnOpenBatchDrawer;
    batchDestinationNames: Record<number, string>;
    rowRefs: MutableRefObject<RowRefs>;
    rowInputRefs: MutableRefObject<RowInputRefs>;
    cellInputRefs?: CellInputRefs;
}

export const useOrderTableRows = ({
    returnSlip,
    searchQuery,
    isCompletedOrHasError,
    isCreditedBatchPending,
    hasDecisionCodes,
    isPublicReturn,
    hasBatchReturnItems,
    showLpn,
    origin,
    handleReturnItemChange,
    handleAddSplitItem,
    handleRemoveSplit,
    handleArrowKeyDown,
    onOpenComments,
    onOpenBatchDrawer,
    batchDestinationNames,
    rowRefs,
    rowInputRefs,
    cellInputRefs,
}: UseOrderTableRowsOptions) => {
    const { t } = useTranslation();
    const getRows = useCallback(
        (
            orderId: string,
            filteredGroup?: {
                orderNumber?: string | null;
                sourceReturnId?: number | null;
                orderLines?: ReturnOrderLine[] | null;
            },
            sourceReturnId?: number | null,
        ): TableRow[] => {
            const orderGroup =
                searchQuery !== ''
                    ? filteredGroup
                    : returnSlip?.returnItems?.find(
                          (group) => group.orderNumber === orderId && group.sourceReturnId === sourceReturnId,
                      );
            const orderLines = orderGroup?.orderLines?.filter((ol) => ol.type !== ReturnOrderLineType.InvoiceCharge);
            if (!orderLines) return [];

            // Bind sourceReturnId into handlers so cells don't need to know about it
            const boundHandleReturnItemChange: HandleReturnItemChange = (
                orderNumber,
                sku,
                orderLineNumber,
                itemId,
                property,
                value,
            ) => handleReturnItemChange(orderNumber, sku, orderLineNumber, itemId, property, value, sourceReturnId);

            const boundHandleAddSplitItem: HandleAddSplitItem = (orderNumber, item, itemId, orderline) =>
                handleAddSplitItem(orderNumber, item, itemId, orderline, sourceReturnId);

            const boundHandleRemoveSplit: HandleRemoveSplit = (
                sku,
                orderNumber,
                orderLineNumber,
                id,
                rowIndex,
                colIndex,
                cellRefPrefix,
            ) =>
                handleRemoveSplit(
                    sku,
                    orderNumber,
                    orderLineNumber,
                    id,
                    rowIndex,
                    colIndex,
                    cellRefPrefix,
                    sourceReturnId,
                );

            let globalRowIndex = 0;

            return orderLines.flatMap(
                (orderline) =>
                    orderline.returnItems?.map((returnItem: ExtendedReturnItem, idx) => {
                        const itemId = returnItem.id ?? returnItem.splitId;
                        const canSplit = canSplitItem(orderline);
                        const currentRowIndex = globalRowIndex++;
                        const handleArrowKeyDownWithOrderId = (
                            e: React.KeyboardEvent,
                            itemId: string | number,
                            rowIndex: number,
                            colIndex: number,
                        ) => handleArrowKeyDown(e, itemId, rowIndex, colIndex, orderId);
                        const cellRefPrefix = `${orderId}-`;

                        // Check if row should be disabled (no quantity available or order line status too low)
                        const qtyAvailable = orderline.quantityAvailableForReturn ?? 0;
                        const isStatusTooLow =
                            orderline.orderLineStatusCode != null && orderline.orderLineStatusCode < 45;
                        const isRowDisabled = qtyAvailable === 0 || isStatusTooLow;
                        const isDisabled = isCompletedOrHasError || isRowDisabled;

                        // Column offset for public return columns (QuantityRequested + Approved)
                        const colOffset = isPublicReturn ? 2 : 0;

                        return {
                            faded: isRowDisabled,
                            cells: [
                                {
                                    el: idx === 0 && (
                                        <OrderLineNumberCell orderLineNumber={orderline.orderLineNumber} />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: idx === 0 && (
                                        <ProductInfoCell
                                            item={orderline}
                                            orderNumber={orderId}
                                            rowRefs={rowRefs}
                                            itemId={itemId}
                                            rowIndex={currentRowIndex}
                                            colIndex={0}
                                            cellInputRefs={cellInputRefs}
                                            handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                            cellRefPrefix={cellRefPrefix}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    ean: orderline.ean ?? '',
                                    sku: orderline.sku ?? '',
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: idx === 0 && <SkuLocationCell sku={orderline.sku} />,
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: (
                                        <ShippedQtyCell
                                            item={returnItem}
                                            canSplit={canSplit}
                                            orderline={orderline}
                                            handleAddSplitItem={boundHandleAddSplitItem}
                                            itemId={itemId}
                                            disabled={isDisabled}
                                            preventRemove={isPublicReturn && (returnItem.quantityRequested ?? 0) > 0}
                                            idx={idx}
                                            handleRemoveSplit={boundHandleRemoveSplit}
                                            splitId={returnItem.splitId}
                                            rowIndex={currentRowIndex}
                                            colIndex={2}
                                            cellInputRefs={cellInputRefs}
                                            handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                            cellRefPrefix={cellRefPrefix}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: (
                                        <ReturnQtyCell
                                            item={returnItem}
                                            orderline={orderline}
                                            itemId={itemId}
                                            rowInputRefs={rowInputRefs}
                                            handleReturnItemChange={boundHandleReturnItemChange}
                                            disabled={isDisabled}
                                            rowIndex={currentRowIndex}
                                            colIndex={3}
                                            cellInputRefs={cellInputRefs}
                                            handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                            cellRefPrefix={cellRefPrefix}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                ...(isPublicReturn
                                    ? [
                                          {
                                              el: <QuantityRequestedCell item={returnItem} itemId={itemId} />,
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                          {
                                              el: (
                                                  <ApprovedStatusCell
                                                      item={returnItem}
                                                      orderline={orderline}
                                                      itemId={itemId}
                                                      handleReturnItemChange={handleReturnItemChange}
                                                      disabled={isDisabled}
                                                      rowIndex={currentRowIndex}
                                                      colIndex={5}
                                                      cellInputRefs={cellInputRefs}
                                                      handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                                      cellRefPrefix={cellRefPrefix}
                                                  />
                                              ),
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                      ]
                                    : []),
                                {
                                    el: (
                                        <ReasonCell
                                            item={returnItem}
                                            itemId={itemId}
                                            orderline={orderline}
                                            handleReturnItemChange={boundHandleReturnItemChange}
                                            disabled={isDisabled}
                                            rowIndex={currentRowIndex}
                                            colIndex={4 + colOffset}
                                            cellInputRefs={cellInputRefs}
                                            handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                            cellRefPrefix={cellRefPrefix}
                                            hasDecisionCodes={hasDecisionCodes}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    flex: 1,
                                    padding: getCellPadding(idx),
                                },
                                ...(showLpn
                                    ? [
                                          {
                                              el: (
                                                  <LpnCell
                                                      item={returnItem}
                                                      itemId={itemId}
                                                      orderline={orderline}
                                                      handleReturnItemChange={handleReturnItemChange}
                                                      disabled={isDisabled}
                                                      rowIndex={currentRowIndex}
                                                      colIndex={5 + colOffset}
                                                      cellInputRefs={cellInputRefs}
                                                      handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                                      cellRefPrefix={cellRefPrefix}
                                                  />
                                              ),
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                      ]
                                    : []),
                                {
                                    el: hasDecisionCodes ? (
                                        <DecisionCodeCell
                                            item={returnItem}
                                            orderline={orderline}
                                            itemId={itemId}
                                            handleReturnItemChange={boundHandleReturnItemChange}
                                            disabled={isDisabled}
                                            rowIndex={currentRowIndex}
                                            colIndex={5 + colOffset}
                                            cellInputRefs={cellInputRefs}
                                            handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                            cellRefPrefix={cellRefPrefix}
                                        />
                                    ) : (
                                        <RefundCell
                                            item={returnItem}
                                            orderline={orderline}
                                            itemId={itemId}
                                            handleReturnItemChange={boundHandleReturnItemChange}
                                            disabled={isDisabled}
                                            rowIndex={currentRowIndex}
                                            colIndex={5 + colOffset}
                                            cellInputRefs={cellInputRefs}
                                            handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                            cellRefPrefix={cellRefPrefix}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                ...(!hasDecisionCodes
                                    ? [
                                          {
                                              el: (
                                                  <ReturnToStockCell
                                                      item={returnItem}
                                                      orderline={orderline}
                                                      itemId={itemId}
                                                      handleReturnItemChange={boundHandleReturnItemChange}
                                                      disabled={isDisabled}
                                                      rowIndex={currentRowIndex}
                                                      colIndex={6 + colOffset}
                                                      cellInputRefs={cellInputRefs}
                                                      handleArrowKeyDown={handleArrowKeyDownWithOrderId}
                                                      cellRefPrefix={cellRefPrefix}
                                                  />
                                              ),
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                      ]
                                    : []),
                                ...(hasBatchReturnItems
                                    ? [
                                          {
                                              el: (
                                                  <BatchReturnIndicatorCell
                                                      item={returnItem}
                                                      onClick={() => onOpenBatchDrawer(orderId, orderline, returnItem)}
                                                      destinationName={
                                                          returnItem.batchReturnDestinationId
                                                              ? batchDestinationNames[
                                                                    returnItem.batchReturnDestinationId
                                                                ]
                                                              : undefined
                                                      }
                                                      disabled={isCompletedOrHasError && !isCreditedBatchPending}
                                                  />
                                              ),
                                              align: alignFlexStart(returnItem),
                                              padding: getCellPadding(idx),
                                          },
                                      ]
                                    : []),
                                {
                                    el: (
                                        <CommentIndicatorCell
                                            item={returnItem}
                                            onClick={() => onOpenComments(orderId, orderline, returnItem)}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: idx === 0 && (
                                        <ReturnsOnThisLineCell
                                            returns={orderline.returnsOnThisLine ?? []}
                                            origin={origin}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: (
                                        <RowActionsMenu
                                            actions={[
                                                {
                                                    icon: faMessageDots,
                                                    label: t('notes'),
                                                    onClick: () => onOpenComments(orderId, orderline, returnItem),
                                                },
                                            ]}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                    overflow: 'visible',
                                },
                            ],
                            bgModified: 'success' as keyof ThemeColors,
                            removeGap: idx !== 0,
                        };
                    }) ?? [],
            );
        },
        [
            returnSlip,
            searchQuery,
            isCompletedOrHasError,
            isPublicReturn,
            origin,
            handleReturnItemChange,
            handleAddSplitItem,
            handleRemoveSplit,
            handleArrowKeyDown,
            onOpenComments,
            rowRefs,
            rowInputRefs,
            cellInputRefs,
            hasDecisionCodes,
            batchDestinationNames,
            hasBatchReturnItems,
            onOpenBatchDrawer,
            showLpn,
            isCreditedBatchPending,
            t,
        ],
    );

    return { getRows };
};

export default useOrderTableRows;
